import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import BuildProjectView from "./BuildProjectView";
import AppFeatureListTileView from "./AppFeatureListTileView";
import D3EFeaturebundles from "../classes/D3EFeaturebundles";
import D3EAppTemplate from "../models/D3EAppTemplate";
import Popup from "./Popup";
import PageNavigator from "../classes/PageNavigator";
import ProjectFeatureListView from "./ProjectFeatureListView";
import ReviewFeatureView from "./ReviewFeatureView";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import SearchComponent from "./SearchComponent";
import Button from "./Button";
import D3EFeature from "../models/D3EFeature";
import CollapseItemView from "./CollapseItemView";
import BuilderProject from "../models/BuilderProject";
import ListWrapper from "../utils/ListWrapper";
import D3EFeatureBundle from "../models/D3EFeatureBundle";
import D3EFeaturebundlesRequest from "../models/D3EFeaturebundlesRequest";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";
import Browser from "../classes/Browser";
import ContainerEx from "./ContainerEx";

type _BaseRefButtonOnPressed = (d3eState: StudioFeaturesPageRefs) => void;

type _CreateProjectButtonOnPressed = (d3eState: StudioFeaturesPageRefs) => void;

export interface StudioFeaturesPageProps extends BaseUIProps {
  key?: string;
  featureList: Array<D3EFeature>;
  templates: Array<D3EAppTemplate>;
  _featureListHash?: number;
  _templatesHash?: number;
}
/// To store state data for StudioFeaturesPage
class StudioFeaturesPageRefs {
  public baseRefButton: BaseRefButtonState = new BaseRefButtonState();
  public createProjectButton: CreateProjectButtonState =
    new CreateProjectButtonState();
  listViewController: ui.ScrollController = new ui.ScrollController();
}

interface CreateProjectButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StudioFeaturesPageRefs;
  _onCreateProjectHandler?: _CreateProjectButtonOnPressed;
}

class CreateProjectButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _CreateProjectButtonWithState extends ObservableComponent<CreateProjectButtonWithStateProps> {
  createProjectButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CreateProjectButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get createProjectButton(): CreateProjectButtonState {
    return this.props.d3eState.createProjectButton;
  }
  public get d3eState(): StudioFeaturesPageRefs {
    return this.props.d3eState;
  }
  public get _onCreateProjectHandler(): _CreateProjectButtonOnPressed {
    return this.props._onCreateProjectHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "createProjectButton",
      null,
      this.createProjectButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "createProjectButton",
        "createProjectButton.",
        "createProjectButton.focus",
        "createProjectButton.hover",
      ],
      this.rebuild
    );
  }
  public createProjectButtonOnEnter(event): void {
    return this.createProjectButton.setHover(true);
  }
  public createProjectButtonOnExit(event): void {
    return this.createProjectButton.setHover(false);
  }
  public createProjectButtonOnFocusChange(val): void {
    return this.createProjectButton.setFocus(val);
  }
  public dispose(): void {
    this.createProjectButton.setHover(false);

    this.createProjectButton.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.all(10.0, new Map()),
      width: 90,
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.createProjectButton.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.createProjectButton.focus
            ? cStyle.tButtonPrimaryButtonDecorationOnFocus
            : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.createProjectButton.disable,
        onPressed: () => {
          this._onCreateProjectHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.createProjectButtonOnFocusChange(val);
        },
        child: TextView({
          data: "Create Project",
          style: new ui.TextStyle({ fontSize: 13 }),
          className: "x460 vc",
        }),
        onEnter: (event) => {
          this.createProjectButtonOnEnter(event);
        },
        onExit: (event) => {
          this.createProjectButtonOnExit(event);
        },
      }),
      className: "x2ae hc vc",
    });
  }
}
function CreateProjectButtonWithState(
  props: CreateProjectButtonWithStateProps
) {
  return React.createElement(_CreateProjectButtonWithState, props);
}

interface BaseRefButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StudioFeaturesPageRefs;
  _onBaseRefHandler?: _BaseRefButtonOnPressed;
}

class BaseRefButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _BaseRefButtonWithState extends ObservableComponent<BaseRefButtonWithStateProps> {
  baseRefButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: BaseRefButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get baseRefButton(): BaseRefButtonState {
    return this.props.d3eState.baseRefButton;
  }
  public get d3eState(): StudioFeaturesPageRefs {
    return this.props.d3eState;
  }
  public get _onBaseRefHandler(): _BaseRefButtonOnPressed {
    return this.props._onBaseRefHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("baseRefButton", null, this.baseRefButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["baseRefButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return Button({
      disable: this.baseRefButton.disable,
      onPressed: () => {
        this._onBaseRefHandler(this.d3eState);
      },
      onFocusChange: (val) => { },
      child: TextView({
        data: "Choose a base",
        style: new ui.TextStyle({
          fontSize: 14,
          color: new ui.Color(0xff9b9fb1),
        }),
        className: "xc77 vc",
      }),
      className: "vc",
    });
  }
}
function BaseRefButtonWithState(props: BaseRefButtonWithStateProps) {
  return React.createElement(_BaseRefButtonWithState, props);
}

class _StudioFeaturesPageState extends ObservableComponent<StudioFeaturesPageProps> {
  static defaultProps = { featureList: [], templates: [] };
  d3eState: StudioFeaturesPageRefs = new StudioFeaturesPageRefs();
  obj: BuilderProject = null;
  searchText: string = "";
  featureBunbdle: Array<D3EFeatureBundle> = ListWrapper.widget(
    this,
    "featureBunbdle"
  );
  filteredFeatures: Array<D3EFeatureBundle> = ListWrapper.widget(
    this,
    "filteredFeatures"
  );
  featureBundleQuery: D3EFeaturebundles = null;
  request: D3EFeaturebundlesRequest = null;
  selectedFeatures: Array<D3EFeature> = ListWrapper.widget(
    this,
    "selectedFeatures"
  );
  selectedFeature: D3EFeature = null;
  isMobileSrc: boolean = false;
  createProjectPopupPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: StudioFeaturesPageProps) {
    super(props);

    this.initState();
  }
  public get featureList(): Array<D3EFeature> {
    return this.props.featureList;
  }
  public get templates(): Array<D3EAppTemplate> {
    return this.props.templates;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.subscribeToList(this.featureList, "featureList");

    this.updateSyncCollProperty("featureList", this.props.featureList);

    this.subscribeToList(this.templates, "templates");

    this.updateSyncCollProperty("templates", this.props.templates);

    this.on(
      ["featureBundleQuery", "featureBundleQuery.items"],
      this.computeFeatureBunbdle
    );

    this.computeFeatureBunbdle();

    this.on(
      [
        "featureBunbdle",
        "featureBunbdle.features",
        "featureBunbdle.features.title",
        "featureBunbdle.title",
        "searchText",
      ],
      this.computeFilteredFeatures
    );

    this.computeFilteredFeatures();

    this.on(
      ["request", "request.offset", "request.orderBy", "request.pageSize"],
      this.computeFeatureBundleQuery
    );

    this.computeFeatureBundleQuery();

    this.on(["featureList"], this.computeSelectedFeature);

    this.computeSelectedFeature();

    this.on(
      [
        "featureList",
        "filteredFeatures",
        "filteredFeatures.features",
        "filteredFeatures.title",
        "isMobileSrc",
        "selectedFeature",
        "selectedFeatures",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: StudioFeaturesPageProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.featureList !== this.props.featureList) {
      this.updateObservableColl(
        "featureList",
        prevProps.featureList,
        this.props.featureList
      );

      this.fire("featureList", this);
    }

    if (prevProps.templates !== this.props.templates) {
      this.updateObservableColl(
        "templates",
        prevProps.templates,
        this.props.templates
      );

      this.fire("templates", this);
    }
  }
  public setObj(val: BuilderProject): void {
    let isValChanged: boolean = this.obj !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("obj", this.obj, val);

    this.obj = val;

    this.fire("obj", this);
  }
  public setSearchText(val: string): void {
    let isValChanged: boolean = this.searchText !== val;

    if (!isValChanged) {
      return;
    }

    this.searchText = val;

    this.fire("searchText", this);
  }
  public setFeatureBunbdle(val: Array<D3EFeatureBundle>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.featureBunbdle,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("featureBunbdle", this.featureBunbdle, val);

    this.featureBunbdle.clear();

    this.featureBunbdle.addAll(val);

    this.fire("featureBunbdle", this);
  }
  public addToFeatureBunbdle(val: D3EFeatureBundle, index: number = -1): void {
    if (index === -1) {
      if (!this.featureBunbdle.contains(val)) this.featureBunbdle.add(val);
    } else {
      this.featureBunbdle.remove(this.featureBunbdle.elementAt(index));

      this.featureBunbdle.add(val);
    }

    this.fire("featureBunbdle", this, val, true);

    this.updateObservable("featureBunbdle", null, val);
  }
  public removeFromFeatureBunbdle(val: D3EFeatureBundle): void {
    this.featureBunbdle.remove(val);

    this.fire("featureBunbdle", this, val, false);

    this.removeObservable("featureBunbdle", val);
  }
  public computeFeatureBunbdle = (): void => {
    try {
      this.setFeatureBunbdle(
        Array.from(
          this.featureBundleQuery !== null ? this.featureBundleQuery.items : []
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeFeatureBunbdle : " + exception.toString()
      );

      this.setFeatureBunbdle([]);
    }
  };
  public setFilteredFeatures(val: Array<D3EFeatureBundle>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.filteredFeatures,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("filteredFeatures", this.filteredFeatures, val);

    this.filteredFeatures.clear();

    this.filteredFeatures.addAll(val);

    this.fire("filteredFeatures", this);
  }
  public addToFilteredFeatures(
    val: D3EFeatureBundle,
    index: number = -1
  ): void {
    if (index === -1) {
      if (!this.filteredFeatures.contains(val)) this.filteredFeatures.add(val);
    } else {
      this.filteredFeatures.remove(this.filteredFeatures.elementAt(index));

      this.filteredFeatures.add(val);
    }

    this.fire("filteredFeatures", this, val, true);

    this.updateObservable("filteredFeatures", null, val);
  }
  public removeFromFilteredFeatures(val: D3EFeatureBundle): void {
    this.filteredFeatures.remove(val);

    this.fire("filteredFeatures", this, val, false);

    this.removeObservable("filteredFeatures", val);
  }
  public computeFilteredFeatures = (): void => {
    try {
      this.setFilteredFeatures(
        Array.from(
          this.featureBunbdle.where(
            (fb) =>
              fb.title.toLowerCase().contains(this.searchText.toLowerCase()) ||
              fb.features.any((f) =>
                f.title.toLowerCase().contains(this.searchText.toLowerCase())
              )
          )
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeFilteredFeatures : " + exception.toString()
      );

      this.setFilteredFeatures([]);
    }
  };
  public setFeatureBundleQuery(val: D3EFeaturebundles): void {
    let isValChanged: boolean = this.featureBundleQuery !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("featureBundleQuery", this.featureBundleQuery, val);

    MessageDispatch.get().dispose(this.featureBundleQuery);

    this.featureBundleQuery = val;

    this.fire("featureBundleQuery", this);
  }
  public computeFeatureBundleQuery = async (): Promise<void> => {
    try {
      this.setFeatureBundleQuery(
        await Query.get().getD3EFeaturebundles(
          UsageConstants.QUERY_GETD3EFEATUREBUNDLES_STUDIOFEATURESPAGE_PROPERTIES_FEATUREBUNDLEQUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeFeatureBundleQuery : " + exception.toString()
      );

      this.setFeatureBundleQuery(null);
    }
  };
  public setRequest(val: D3EFeaturebundlesRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setSelectedFeatures(val: Array<D3EFeature>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.selectedFeatures,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("selectedFeatures", this.selectedFeatures, val);

    this.selectedFeatures.clear();

    this.selectedFeatures.addAll(val);

    this.fire("selectedFeatures", this);
  }
  public addToSelectedFeatures(val: D3EFeature, index: number = -1): void {
    if (index === -1) {
      if (!this.selectedFeatures.contains(val)) this.selectedFeatures.add(val);
    } else {
      this.selectedFeatures.remove(this.selectedFeatures.elementAt(index));

      this.selectedFeatures.add(val);
    }

    this.fire("selectedFeatures", this, val, true);

    this.updateObservable("selectedFeatures", null, val);
  }
  public removeFromSelectedFeatures(val: D3EFeature): void {
    this.selectedFeatures.remove(val);

    this.fire("selectedFeatures", this, val, false);

    this.removeObservable("selectedFeatures", val);
  }
  public setSelectedFeature(val: D3EFeature): void {
    let isValChanged: boolean = this.selectedFeature !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("selectedFeature", this.selectedFeature, val);

    this.selectedFeature = val;

    this.fire("selectedFeature", this);
  }
  public computeSelectedFeature = (): void => {
    try {
      this.setSelectedFeature(
        this.featureList.isNotEmpty ? this.featureList[0] : null
      );
    } catch (exception) {
      console.log(
        " exception in computeSelectedFeature : " + exception.toString()
      );

      this.setSelectedFeature(null);
    }
  };
  public setIsMobileSrc(val: boolean): void {
    let isValChanged: boolean = this.isMobileSrc !== val;

    if (!isValChanged) {
      return;
    }

    this.isMobileSrc = val;

    this.fire("isMobileSrc", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      children: [
        ui.Column({
          children: [
            ui.Row({
              children: [
                ui.GestureDetector({
                  onTap: () => {
                    Browser.open("https://new.d3e.studio");
                  },
                  child: ContainerEx.from({
                    margin: ui.EdgeInsets.only({ left: 10 }),
                    child: ui.AssetImage({
                      path: "images/headerlogo.png",
                      width: 90,
                      height: 50,
                      fit: ui.BoxFit.contain,
                      className: "x88 hc vc",
                      key: "0",
                    }),
                  })
                })
              ],
              className: "xa5 hc h",
              key: "0",
            }),
            ui.Column({
              mainAxisSize: ui.MainAxisSize.min,
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    10.0,
                    new Map()
                  ),
                  child: SearchComponent({
                    placeholder: "Search Feature",
                    onChanged: (value) => {
                      this.searchTextHandler(value, this.d3eState);
                    },
                  }),
                  key: "0",
                  className: "x62 hc h",
                }),
                ui.ListView({
                  controller: this.d3eState.listViewController,
                  children: [
                    this.filteredFeatures.expand((bundleItem) => [
                      ui.Container({
                        margin: ui.EdgeInsets.symmetric({
                          horizontal: 10.0,
                          vertical: 0.0,
                          transitions: new Map(),
                        }),
                        child: CollapseItemView({
                          title: bundleItem.title,
                          child: ui.Wrap({
                            spacing: 5,
                            children: [
                              bundleItem.features.expand((featureItem) => [
                                AppFeatureListTileView({
                                  obj: featureItem,
                                  isAddedFeature:
                                    this.featureList.contains(featureItem),
                                  onAddFeature: (item) => {
                                    this.onAddHandler(
                                      item,
                                      featureItem,
                                      bundleItem,
                                      this.d3eState
                                    );
                                  },
                                  onRemoveFeature: (item) => {
                                    this.onRemoveHandler(
                                      item,
                                      featureItem,
                                      bundleItem,
                                      this.d3eState
                                    );
                                  },
                                  onViewFeature: (item) => {
                                    this.onViewFeatureHandler(
                                      item,
                                      featureItem,
                                      bundleItem,
                                      this.d3eState
                                    );
                                  },
                                  className: "x409 hc h",
                                  key: featureItem?.ident,
                                }),
                              ]),
                            ],
                            className: "xa2 hc h",
                          }),
                        }),
                        key: bundleItem?.ident,
                        className: "xd02 hc h",
                      }),
                    ]),
                  ],
                  className: "xc26 hc vc h v",
                  key: "1",
                }),
              ],
              className: "xd05 hc vc h v",
              key: "1",
            }),
          ],
          className: "x69 hc vc v",
          key: "0",
        }),
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                BaseRefButtonWithState({
                  d3eState: this.d3eState,
                  _onBaseRefHandler: this.onBaseRefHandler,
                  key: "0",
                }),
                CreateProjectButtonWithState({
                  d3eState: this.d3eState,
                  _onCreateProjectHandler: this.onCreateProjectHandler,
                  key: "1",
                }),
              ],
              className: "x462 hc vc h",
              key: "0",
            }),
            this.selectedFeature !== null
              ? ui.Column({
                mainAxisAlignment: ui.MainAxisAlignment.center,
                crossAxisAlignment: ui.CrossAxisAlignment.center,
                children: [
                  ui.Container({
                    expand: true,
                    child: ReviewFeatureView({
                      obj: this.selectedFeature,
                      onChangeView: (isMobileView) => {
                        this.onChangeViewHandler(isMobileView, this.d3eState);
                      },
                    }),
                    key: "0",
                    className: "x486 hc vc h v",
                  }),
                ],
                className: "x70 hc vc h v",
              })
              : [],
            this.selectedFeatures.isNotEmpty
              ? ui.Row({
                mainAxisAlignment: ui.MainAxisAlignment.start,
                crossAxisAlignment: ui.CrossAxisAlignment.center,
                children: [
                  ui.Container({
                    margin: ui.EdgeInsets.all(10.0, new Map()),
                    child: ProjectFeatureListView({
                      features: this.selectedFeatures,
                      isMobileSrc: this.isMobileSrc,
                      onReviewFeature: (feature) => {
                        this.onReviewFeatureHandler(feature, this.d3eState);
                      },
                    }),
                    key: "0",
                    className: "xe3 vc",
                  }),
                ],
                className: "xf36 hc vc h",
              })
              : [],
          ],
          className: "x14 hc vc h v",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "StudioFeaturesPage hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new D3EFeaturebundlesRequest({ pageSize: 1000, offset: 0, orderBy: "" })
    );

    this.setSelectedFeatures(this.featureList);
  };
  public onAddHandler = (
    item: D3EFeature,
    featureItem: D3EFeature,
    bundleItem: D3EFeatureBundle,
    d3eState: StudioFeaturesPageRefs
  ): void => {
    this.selectedFeatures.add(item);
  };
  public onRemoveHandler = (
    item: D3EFeature,
    featureItem: D3EFeature,
    bundleItem: D3EFeatureBundle,
    d3eState: StudioFeaturesPageRefs
  ): void => {
    this.selectedFeatures.remove(item);
  };
  public onViewFeatureHandler = (
    item: D3EFeature,
    featureItem: D3EFeature,
    bundleItem: D3EFeatureBundle,
    d3eState: StudioFeaturesPageRefs
  ): void => {
    this.setSelectedFeature(item);
  };
  public searchTextHandler = (
    value: string,
    d3eState: StudioFeaturesPageRefs
  ): void => {
    this.setSearchText(value);
  };
  public onReviewFeatureHandler = (
    feature: D3EFeature,
    d3eState: StudioFeaturesPageRefs
  ): void => {
    this.setSelectedFeature(feature);
  };
  public onChangeViewHandler = (
    isMobileView: boolean,
    d3eState: StudioFeaturesPageRefs
  ): void => {
    this.setIsMobileSrc(isMobileView);
  };
  public onCreateProjectHandler = (d3eState: StudioFeaturesPageRefs): void => {
    this.setObj(new BuilderProject());

    this.showCreateProjectPopup();
  };
  public onBaseRefHandler = (d3eState: StudioFeaturesPageRefs): void => {
    this.navigator.pushStudioAppsPage({ replace: false });
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.featureBundleQuery);

    this.createProjectPopupPopup?.dispose();

    super.dispose();
  }
  public showCreateProjectPopup(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.createProjectPopupPopup?.dispose();

    this.createProjectPopupPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 500,
        child: BuildProjectView({
          obj: this.obj,
          templates: this.templates,
          features: this.selectedFeatures,
        }),
        className: "xb9 hc vc",
      }),
    });

    this.createProjectPopupPopup.showPopup(this.context);
  }
  public hideCreateProjectPopup(): void {
    this.createProjectPopupPopup?.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get baseRefButton() {
    return this.d3eState.baseRefButton;
  }
  public get createProjectButton() {
    return this.d3eState.createProjectButton;
  }
}
export default function StudioFeaturesPage(props: StudioFeaturesPageProps) {
  return React.createElement(
    _StudioFeaturesPageState,
    { ..._StudioFeaturesPageState.defaultProps, ...props },
    ListWrapper.fromInput<D3EFeature>(props.featureList, "featureList"),
    ListWrapper.fromInput<D3EAppTemplate>(props.templates, "templates")
  );
}
